import { ArrowLeft, CornerUpRightIcon, Loader2Icon } from "lucide-react";
import { imageAssetUrl } from "@/utils/url";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormInput,
  FormItem,
  FormMessage,
  Input,
  V2Label,
  V2Loader,
} from "web-design-system";
import { Client } from "creso-core/types";
import { useCasUpload, useMfCentralStatus } from "creso-core/hooks";
import { useState, useEffect } from "react";
import { handleError, handleFormValidationErrors } from "@/utils/error";
import {
  SyncingLottieAnimation,
  SuccessLottieAnimation,
  WarningLottieAnimation,
  FileUpload,
} from "web-core/components";
import { CopyButton } from "@core/components/copy-btn";
import { useForm } from "react-hook-form";
import { object, string, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { ImportType } from "./dialog";

type ImportState =
  | "not-started"
  | "uploading"
  | "syncing"
  | "success"
  | "error";

const casUploadFormSchema = object({
  file: z
    .instanceof(File)
    .nullable()
    .refine((file) => file !== null, {
      message: "File is required",
      path: ["file"],
    }),
  password: string().min(1, { message: "Password is required" }),
});

export function CasUpload(props: {
  client: Client;
  onChangeImportType: (type: ImportType) => void;
}) {
  const { client } = props;
  const [importState, setImportState] = useState<ImportState>("not-started");
  const { mutateAsync: uploadCasStatement, isPending: isUploading } =
    useCasUpload();

  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(casUploadFormSchema),
    defaultValues: {
      password: "",
    },
  });

  const { data: mfCentralStatus, refetch } = useMfCentralStatus(
    client?.investment_account ?? ""
  );

  useEffect(() => {
    if (importState === "syncing") {
      if (mfCentralStatus?.status === "SUCCESS") {
        setImportState("success");
      } else if (mfCentralStatus?.status === "FAILED") {
        setImportState("error");
      }
    }
  }, [importState, mfCentralStatus?.status]);

  const handleUpload = async (data: z.infer<typeof casUploadFormSchema>) => {
    if (!client.investment_account) return;
    try {
      setImportState("uploading");
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("password", data.password);
      formData.append("investment_account_guid", client.investment_account);
      await uploadCasStatement(formData);
    } catch (error) {
      setImportState("not-started");
      if (!handleFormValidationErrors(form, error)) {
        handleError(error, "Failed to upload CAS statement");
      }
      return;
    }
    const { error } = await refetch();
    if (error) {
      setImportState("error");
    } else {
      setImportState("syncing");
    }
  };

  return (
    <V2Loader isLoading={false}>
      <div className="flex flex-row">
        <div className="flex px-3 pt-5 min-w-140 w-140 pb-6 flex-col">
          <div className="flex flex-row gap-2 items-center">
            <Button
              onClick={() => props.onChangeImportType("mfcentral")}
              size="icon"
              variant="ghost"
            >
              <ArrowLeft />
            </Button>
            <span className="text-lg font-semibold">{`Step 1: Get ${client.name}'s CAS statement`}</span>
          </div>
          <div className="px-3 mt-4 flex flex-col">
            <div className="flex flex-col gap-1.5">
              <V2Label className="font-normal">Visit this link</V2Label>
              <div className="items-center flex flex-1 gap-2">
                <Input
                  readOnly
                  disabled
                  value="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement"
                />
                <CopyButton text="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement" />
              </div>
            </div>
            <span className="my-4 text-sm">
              and fill the form as explained below:
            </span>
            <img src={imageAssetUrl("cas-upload.webp")} className="min-w-128" />
            <span className="mt-9 text-xs text-muted-foreground">
              Once the client submits this form on CAMS, they will receive the
              statement via email from CAMS within 15 minutes (may take up to 24
              hours).
            </span>
          </div>
        </div>
        <div className="flex p-6 bg-card flex-col gap-5 w-90 min-w-90">
          <span className="font-semibold text-lg">Step 2: Share CAS file</span>
          {(importState === "not-started" || importState === "uploading") && (
            <div className="flex flex-col flex-1 gap-5 w-full">
              <div className="flex flex-col gap-2">
                <div className="rounded-lg flex flex-row gap-3 bg-secondary p-3">
                  <div className="bg-background items-center flex flex-shrink-0 justify-center rounded-lg size-9">
                    <CornerUpRightIcon className="size-5" />
                  </div>
                  <span className="text-sm">
                    <span className="font-semibold">Forward</span> the CAS mail
                    directly to{" "}
                    <span className="font-semibold">support@creso.in</span>
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  For this, password must be PAN number in all caps.
                </span>
              </div>
              <div className="h-px bg-border relative">
                <span className="text-sm bg-card absolute text-center px-3 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
                  Or
                </span>
              </div>
              <Form {...form}>
                <form
                  className="flex flex-col flex-1 gap-5"
                  onSubmit={form.handleSubmit(handleUpload)}
                >
                  <FormField
                    className="flex-1 flex"
                    control={form.control}
                    name="file"
                    render={({ field }) => {
                      return (
                        <FormItem className="relative flex space-y-0 size-full">
                          <FormControl>
                            <FileUpload
                              acceptedTypes={["application/pdf"]}
                              onChange={(file) => {
                                field.onChange(file);
                                form.clearErrors("file");
                              }}
                            />
                          </FormControl>
                          <FormMessage className="absolute text-center bottom-4 left-0 right-0" />
                        </FormItem>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormInput
                        withEye
                        label="Document password"
                        placeholder="Enter password"
                        type="password"
                        {...field}
                      />
                    )}
                  />
                  <Button
                    type="submit"
                    disabled={
                      importState !== "not-started" || !form.watch("file")
                    }
                    className="w-full mt-auto"
                  >
                    {isUploading && <Loader2Icon className="animate-spin" />}
                    {isUploading ? "Uploading..." : "Upload"}
                  </Button>
                </form>
              </Form>
            </div>
          )}
          {importState === "syncing" && (
            <div className="flex flex-1 flex-col items-center pt-20 justify-start">
              <SyncingLottieAnimation loop={false} height={100} width={100} />
              <span className="text-muted-foreground text-sm text-center">
                Syncing...
              </span>
              <Button disabled className="w-full mt-auto">
                <Loader2Icon className="animate-spin" />
                Uploading...
              </Button>
            </div>
          )}
          {importState === "success" && (
            <div className="flex flex-1 flex-col items-center pt-20 justify-start">
              <SuccessLottieAnimation loop={false} height={100} width={100} />
              <span className="text-muted-foreground text-sm text-center">
                Successfully synced
              </span>
              <Button
                onClick={() => router.history.back()}
                className="w-full mt-auto"
              >
                Done
              </Button>
            </div>
          )}
          {importState === "error" && (
            <div className="flex flex-1 flex-col items-center pt-20 justify-start">
              <WarningLottieAnimation loop={false} height={100} width={100} />
              <span className="text-muted-foreground text-sm text-center">
                Upload failed
              </span>
              <Button
                onClick={() => setImportState("not-started")}
                className="w-full mt-auto"
              >
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    </V2Loader>
  );
}
